import React from 'react';
import { Modal, Button, Card, Badge, Row, Col } from 'react-bootstrap';
import { formatDateTime } from '../../../../utils/dateUtils';
import type { CampaignDetailModalProps } from './types';
import { getCampaignStatusColor, getCampaignStatusText } from './types';

// Helper function to format date/time with UTC label
const formatDateTimeUTC = (date: Date): string => {
  const formatted = formatDateTime(date.toISOString());
  return `${formatted} (UTC)`;
};

const CampaignDetailModal: React.FC<CampaignDetailModalProps> = ({ show, campaign, onHide, onEdit }) => {
  if (!campaign) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Campaign Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h3>{campaign.name}</h3>
            <Badge bg={getCampaignStatusColor(campaign.status)} className="fs-6">
              {getCampaignStatusText(campaign.status)}
            </Badge>
          </div>

          <Row className="mb-4">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="mb-0">Campaign Period</h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <strong>Start Date:</strong> {formatDateTimeUTC(campaign.startAt)}
                  </div>
                  <div>
                    <strong>End Date:</strong> {formatDateTimeUTC(campaign.endAt)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="mb-0">System Information</h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <strong>Campaign ID:</strong> {campaign.id}
                  </div>
                  <div className="mb-2">
                    <strong>Created:</strong> {campaign.createdAt ? formatDateTimeUTC(campaign.createdAt) : '-'}
                  </div>
                  <div>
                    <strong>Last Updated:</strong> {campaign.updatedAt ? formatDateTimeUTC(campaign.updatedAt) : '-'}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header>
              <h6 className="mb-0">Campaign Status Information</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center">
                <Badge bg={getCampaignStatusColor(campaign.status)} className="me-2">
                  {getCampaignStatusText(campaign.status)}
                </Badge>
                <span className="text-muted">
                  {campaign.status === 'Published' && 'This campaign is currently published and visible'}
                  {campaign.status === 'Draft' && 'This campaign is in draft mode and not published'}
                </span>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={onEdit}>
          Edit Campaign
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CampaignDetailModal;
